import type { App } from 'vue';
import type { Router } from 'vue-router';

declare global {
  interface Window {
    umami?: {
      // Umami v2: an event name with data, or a function that rewrites the
      // pageview payload (used for the router pageviews below).
      track: {
        (eventName: string, data?: Record<string, unknown>): void;
        (payload: (props: Record<string, unknown>) => Record<string, unknown>): void;
      };
      identify: (data: Record<string, unknown>) => void;
    };
  }
}

const eventQueue: Array<() => void> = [];

export function installAnalytics(app: App, router: Router) {
  const websiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID;
  const scriptUrl = import.meta.env.VITE_UMAMI_SCRIPT_URL;
  const appVariant = import.meta.env.VITE_APP_VARIANT;

  const track = (eventName: string, eventData?: Record<string, unknown>) => {
    // 1. Forward event to parent window if in an iframe (execute immediately, independent of Umami)
    if (window.self !== window.top) {
      window.parent.postMessage(
        {
          type: 'booking-event',
          event: eventName,
          step: eventData?.step || eventData?.fromStep || eventData?.toStep,
          variant: appVariant,
        },
        '*',
      );
    }

    // 2. Queue or execute Umami tracking
    const executeUmami = () => {
      window.umami?.track(eventName, {
        variant: appVariant,
        ...eventData,
      });
    };
    if (window.umami) {
      executeUmami();
    } else if (websiteId && scriptUrl) {
      eventQueue.push(executeUmami);
    }
  };

  app.config.globalProperties.$track = track;
  app.provide('track', track);

  if (!websiteId || !scriptUrl) {
    return;
  }

  // Dynamically inject the Umami script element into <head>
  const script = document.createElement('script');
  script.async = true;
  script.defer = true;
  script.src = scriptUrl;
  script.setAttribute('data-website-id', websiteId);
  script.setAttribute('data-auto-track', 'false'); // Let's track manually so we control parameters and variant attributes
  script.setAttribute('data-performance', 'true'); // Enable Core Web Vitals tracking

  script.onload = () => {
    while (eventQueue.length > 0) {
      const fn = eventQueue.shift();
      if (fn) fn();
    }
  };

  document.head.appendChild(script);

  // Auto-track pageviews using vue-router navigation guards
  router.afterEach((to) => {
    const execute = () => {
      if (window.umami) {
        const pageTitle = (to.meta?.title as string) || to.name?.toString() || to.path;
        // In Umami v2, passing a function tracks a pageview and allows overriding default properties
        window.umami.track((props: Record<string, unknown>) => ({
          ...props,
          url: to.fullPath,
          title: pageTitle,
        }));
        // Track pageview variant as a separate custom event to easily filter if needed
        window.umami.track('page_view', {
          variant: appVariant,
          path: to.fullPath,
          title: pageTitle,
        });
      }
    };

    if (window.umami) {
      execute();
    } else {
      eventQueue.push(execute);
    }
  });
}

export function useAnalytics() {
  const appVariant = import.meta.env.VITE_APP_VARIANT;

  const trackEvent = (eventName: string, eventData?: Record<string, unknown>) => {
    // 1. Forward event to parent window if in an iframe
    if (window.self !== window.top) {
      window.parent.postMessage(
        {
          type: 'booking-event',
          event: eventName,
          step: eventData?.step || eventData?.fromStep || eventData?.toStep,
          variant: appVariant,
        },
        '*',
      );
    }

    // 2. Queue or execute Umami tracking
    const executeUmami = () => {
      window.umami?.track(eventName, {
        variant: appVariant,
        ...eventData,
      });
    };
    if (window.umami) {
      executeUmami();
    } else if (import.meta.env.VITE_UMAMI_WEBSITE_ID) {
      eventQueue.push(executeUmami);
    }
  };

  return {
    trackEvent,
  };
}
